import React, { CSSProperties, useMemo } from "react";
import { Canvas } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import * as THREE from "three";
import Model from "~/Model";
import { createReceptionIdleMotionStrategy } from "~/reception/motion/receptionIdleMotion";

type ReceptionCharacterCanvasProps = {
  /**
   * Canvas に適用する追加スタイル。
   * 既定の `width: "100%"` や `height: "320px"` にマージされ、親コンテナに合わせたサイズ調整を想定する。
   * 例: `{ height: "100%" }`
   */
  canvasStyle?: CSSProperties;
  /**
   * Modelに渡すidleSwitchProbability。デフォルト1。
   * 受付ではクリップ終了ごとに必ず待機モーションへ遷移させ、
   * お辞儀（ワンショット）の永久リピートを防ぐため既定を 1 とする。
   */
  idleSwitchProbability?: number;
};

const defaultCanvasStyle: CSSProperties = {
  borderRadius: "10px",
  display: "block",
  height: "320px",
  width: "100%",
};

const cameraConfig = {
  fov: 20,
  near: 0.1,
  far: 100,
  position: [0, -0.04, 0.5] as [number, number, number],
};

const glConfig = {
  antialias: true,
  alpha: true,
  outputColorSpace: THREE.SRGBColorSpace,
  toneMapping: THREE.NeutralToneMapping,
  toneMappingExposure: 1.0,
};

/**
 * 受付会話画面で利用するキャラクター描画用 Canvas。
 * 親レイアウトから `canvasStyle` を受け取りつつ、既定の見た目と Three.js 設定を維持して表示する。
 */
const ReceptionCharacterCanvas: React.FC<ReceptionCharacterCanvasProps> = ({
  canvasStyle,
  idleSwitchProbability = 1,
}) => {
  // 受付専用の待機モーションストラテジー。
  // sequential モードの巡回 index を保持するため、useMemo で 1 回だけ生成する。
  const idleMotionStrategy = useMemo(() => createReceptionIdleMotionStrategy(), []);

  return (
    <div className="reception-conversation-character-wrap">
      <Canvas
        className="reception-conversation-canvas"
        camera={cameraConfig}
        flat
        style={{ ...defaultCanvasStyle, ...canvasStyle }}
        gl={glConfig}
      >
        {/* eslint-disable react/no-unknown-property */}
        <directionalLight
          position-x={5}
          position-y={10}
          position-z={10}
          color={0xffffff}
          intensity={3.0}
        />
        <ambientLight color={0xffffff} intensity={0.5} />
        {/* eslint-enable react/no-unknown-property */}
        <OrbitControls enableZoom={false} enableRotate={false} enablePan={false} />
        <Model
          onViewed={() => undefined}
          forceReparse={false}
          idleSwitchProbability={idleSwitchProbability}
          idleMotionStrategy={idleMotionStrategy}
        />
      </Canvas>
    </div>
  );
};

export default ReceptionCharacterCanvas;
